const MILLION_DIVISOR = 1_000_000;

function trimTrailingZeros(value: string): string {
  return value.replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
}

function formatNumericValue(
  value: number,
  options: { prefix?: string; preserveCurrency?: boolean } = {}
): string {
  const sign = value < 0 ? '-' : '';
  const absoluteValue = Math.abs(value);
  const formattedMagnitude = trimTrailingZeros(
    absoluteValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  );
  const currencyPrefix = options.preserveCurrency === true || options.prefix === '$'
    ? '$'
    : (options.prefix || '');

  return `${sign}${currencyPrefix}${formattedMagnitude}`;
}

function parseNumericValue(value: any): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const rawValue = String(value).trim();
  if (!rawValue) {
    return null;
  }

  const sanitized = rawValue.replace(/,/g, '').replace(/[^0-9.-]/g, '');
  if (!sanitized || sanitized === '-' || sanitized === '.' || sanitized === '-.') {
    return null;
  }

  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
}

function isPercentageContext(context: string = '', value: any = ''): boolean {
  const normalizedContext = String(context || '').toLowerCase();
  const normalizedValue = String(value ?? '').toLowerCase();

  return normalizedContext.includes('%')
    || normalizedContext.includes('percent')
    || normalizedContext.includes('percentage')
    || normalizedValue.includes('%');
}

function shouldDisplayInMillions(context: string = ''): boolean {
  const normalizedContext = String(context || '').toLowerCase();
  if (!normalizedContext || isPercentageContext(normalizedContext)) {
    return false;
  }

  return [
    'sales',
    'impact',
    'value',
    'revenue',
    'dollar',
    'amount',
    'price',
    'cost',
    'margin',
    'units',
    'volume'
  ].some((token) => normalizedContext.includes(token));
}

export function formatValueInMillions(
  value: any,
  options: { prefix?: string; preserveCurrency?: boolean } = {}
): string {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) {
    return '-';
  }

  const rawValue = String(value ?? '');
  const hasCurrency = options.preserveCurrency === true || rawValue.includes('$') || options.prefix === '$';
  const scaledValue = numericValue / MILLION_DIVISOR;
  const absScaledValue = Math.abs(scaledValue);

  if (absScaledValue < 1) {
    return formatNumericValue(numericValue, {
      prefix: options.prefix,
      preserveCurrency: hasCurrency,
    });
  }

  const formattedMagnitude = absScaledValue.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const sign = scaledValue < 0 ? '-' : '';
  const currencyPrefix = hasCurrency ? '$' : (options.prefix || '');

  return `${sign}${currencyPrefix}${formattedMagnitude}M`;
}

export function formatDisplayValueByContext(
  value: any,
  context: string = '',
  options: { forceCurrency?: boolean } = {}
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const rawValue = String(value).trim();
  if (!rawValue) {
    return '';
  }

  const numericValue = parseNumericValue(value);
  if (numericValue === null) {
    return rawValue;
  }

  if (isPercentageContext(context, value)) {
    return `${trimTrailingZeros(
      numericValue.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      })
    )}%`;
  }

  if (shouldDisplayInMillions(context) || options.forceCurrency || rawValue.includes('$')) {
    return formatValueInMillions(value, {
      preserveCurrency: options.forceCurrency || rawValue.includes('$'),
    });
  }

  return trimTrailingZeros(
    numericValue.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  );
}

export function formatStat(
  value: any,
  key: string = '',
  prefix: string = ''
): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  if (key.includes('%')) {
    return `${numericValue.toFixed(1)}${prefix}`;
  }

  return formatValueInMillions(numericValue, { prefix });
}

export function formatStatOneDecimal(
  value: any,
  key: string = '',
  prefix: string = ''
): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  if (key.includes('%')) {
    return `${numericValue.toFixed(1)}${prefix}`;
  }

  const scaledValue = numericValue / MILLION_DIVISOR;
  const magnitude = trimTrailingZeros(Math.abs(scaledValue).toFixed(1));
  const sign = scaledValue < 0 ? '-' : '';

  return `${sign}${prefix}${magnitude}M`;
}

export function commaSeparatedValue(value: any): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = Number(value);
  if (isNaN(numericValue)) return '-';

  return numericValue.toLocaleString('en-US');
}

export function roundToNearestInteger(value: number): number {
  return Math.round(value);
}

export function formatDecimalWithCommas(value: number | string): string {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) return '-';

  return Math.round(numericValue).toLocaleString('en-US');
}

export function formatWholeNumberWithCommas(value: number | string): string {
  return formatDecimalWithCommas(value);
}

export function oneDecimalWithCommas(value: number | string): string {
  const numericValue = parseNumericValue(value);
  if (numericValue === null) return '-';

  const scaledValue = numericValue / MILLION_DIVISOR;
  const magnitude = Math.abs(scaledValue).toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  const sign = scaledValue < 0 ? '-' : '';

  return `${sign}${magnitude}M`;
}
